import { CONST_ROOT_COMPONENT_INDEX, IComponent, ILink } from "@multiweb/picasso";
import { bridge } from "@multiweb/reactCore/bridge";
import { EMPTY_OBJECT } from "@multiweb/reactUtils";
import { ActionType, ParticipantType, TriggerType } from "./enums";
import { LOC_ATTRIBUTE_CHANGE, LOC_ATTRIBUTE_VALUE_CHANGE, LOC_CHANGE_OF_BINDING_ATTRIBUTE, LOC_CREATION_OF_DOCUMENT, LOC_DEFINITION_PARTICIPANT, LOC_EMAIL, LOC_EXECUTING_METHOD, LOC_FORM_OPENING, LOC_INCREMENTING_VALUE, LOC_MENU, LOC_OWNER, LOC_ROLE, LOC_SENDING_OF_WF_MESSAGE, LOC_SETTING_VALUE, LOC_SMS, LOC_START_OF_SCRIPT, LOC_SYSTEM, LOC_USER, LOC_WORKFLOW_MESSAGE } from "./locKeys";
import { IAction, IActivity, IParticipant, IPosition, ISet, ITransition, ITrigger } from "./types";
import { UiActivity } from "./UiActivity";
import { UiSet } from "./UiSet";
import { UiTransition } from "./UiTransition";
import { UiWorkflow } from "./UiWorkflow";
import { Workflow } from "./workflowModel";
import { Action, CallMethod, CallScript, DateTimePrecision, FormType, IncrementDateTime, IncrementNumber, MessageFormat, OpenForm, SendMail, SendMessageRecipientType, SendSMS, SendSmsRecipientType, SendWFMessage, SetAttribute, SetObjectAttribute, SetValue } from "./workflowModel/Actions";
import { DefinitionParticipant, Owner, Role, System, User } from "./workflowModel/Participants";
import { AttributeDataChange, Menu, NewObject, WfMessage } from "./workflowModel/Triggers";
import {
    CONST_ACTION_COMMENT_AT,
    CONST_ACTION_CONDITION_AT,
    CONST_ATTRIBUTE_DATA_CHANGE_ATTR_NAME_AT,
    CONST_ATTRIBUTE_DATA_CHANGE_CONDITION_AT,
    CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT,
    CONST_CALL_METHOD_INSERT_ACTUAL_OBJECT_AT,
    CONST_CALL_METHOD_INSERT_PARAM_FORM_OBJECT_AT,
    CONST_CALL_METHOD_METHOD_NAME_AT,
    CONST_CALL_METHOD_STATIC_AT,
    CONST_CALL_SCRIPT_SCRIPT_AT,
    CONST_DEFINITION_PARTICIPANT_CONDITION_AT,
    CONST_DEFINITION_PARTICIPANT_NAME_AT,
    CONST_DEFINITION_PARTICIPANT_ROLE_NAME_AT,
    CONST_INCREMENT_DATE_TIME_ATTR_NAME_AT,
    CONST_INCREMENT_DATE_TIME_PRECISION_AT,
    CONST_INCREMENT_DATE_TIME_VALUE_AT,
    CONST_INCREMENT_NUMBER_ATTR_NAME_AT,
    CONST_INCREMENT_NUMBER_VALUE_AT,
    CONST_MENU_CAPTIONS_EL,
    CONST_MENU_CHOOSE_FROM_USERS_DEFAULT,
    CONST_MENU_CODE_NAME,
    CONST_MENU_CONFIRM_MESSAGES_EL,
    CONST_MENU_ICON_NAME,
    CONST_MENU_IDENTIFIER_AT,
    CONST_MENU_LINK_ATTR_FOR_SEL_OBJ_AT,
    CONST_MENU_MASTER_NODE_AT,
    CONST_MENU_MULTI_ACTION_ENABLE_AT,
    CONST_MENU_PARAM_FORM_NAME_AT,
    CONST_MENU_PARTICIPANTS_SELECTING_AT,
    CONST_MENU_SET_VALUES_AT,
    CONST_MENU_VALIDATE_POST_REQ_AT,
    CONST_MENU_VALIDATE_RECOMMENDED_AT,
    CONST_OPEN_FORM_FORM_CLASS_AT,
    CONST_OPEN_FORM_FORM_CONDITION_AT,
    CONST_OPEN_FORM_FORM_TYPE_AT,
    CONST_OPEN_FORM_MASTER_LINK_TO_ATTR_AT,
    CONST_OPEN_FORM_REPORT_NAME_AT,
    CONST_SEND_MAIL_ATTR_NAME_AT,
    CONST_SEND_MAIL_CLASS_NAME_AT,
    CONST_SEND_MAIL_DATA_CONDITION_AT,
    CONST_SEND_MAIL_DOCUMENT_ATTRIBUTE_AT,
    CONST_SEND_MAIL_DOCUMENT_CLASS_NAME_AT,
    CONST_SEND_MAIL_DOCUMENT_CONDITION_AT,
    CONST_SEND_MAIL_DOCUMENT_CONDITION_PARAMS_AT,
    CONST_SEND_MAIL_FORMAT_AT,
    CONST_SEND_MAIL_RECIPIENT_AT,
    CONST_SEND_MAIL_RECIPIENT_TYPE_AT,
    CONST_SEND_MAIL_REPORT_TEMPLATE_AT,
    CONST_SEND_MAIL_SEND_POSTPONE_AT,
    CONST_SEND_MAIL_SUBJECT_AT,
    CONST_SEND_MAIL_SUMMARY_MAIL_AT,
    CONST_SEND_MAIL_TEXT_AT,
    CONST_SEND_SMS_ATTR_NAME_AT,
    CONST_SEND_SMS_CLASS_NAME_AT,
    CONST_SEND_SMS_DATA_CONDITION_AT,
    CONST_SEND_SMS_RECIPIENT_AT,
    CONST_SEND_SMS_RECIPIENT_TYPE_AT,
    CONST_SEND_SMS_TEXT_AT,
    CONST_SEND_WF_MESSAGE_AFTER_SAVE_AT,
    CONST_SEND_WF_MESSAGE_MESSAGE_NAME_AT,
    CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_AT,
    CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_PARAMS_AT,
    CONST_SEND_WF_MESSAGE_TARGET_OBJECT_NAME_AT,
    CONST_SET_ATTRIBUTE_ATTR_NAME_AT,
    CONST_SET_ATTRIBUTE_VALUE_EL,
    CONST_SET_ATTRIBUTE_VALUE_TYPE,
    CONST_SET_OBJECT_ATTRIBUTE_ATTR_NAME_AT,
    CONST_SET_OBJECT_ATTRIBUTE_MASTER_ATTR_NAME_AT,
    CONST_SET_OBJECT_ATTRIBUTE_MASTER_CLASS_NAME_AT,
    CONST_SET_OBJECT_ATTRIBUTE_VALUE_EL,
    CONST_SET_VALUE_NAME_AT,
    CONST_SET_VALUE_VALUE_EL,
    CONST_WFMESSAGE_DEACTIVATING_AT,
    CONST_WFMESSAGE_MESSAGE_NAME_AT,
} from "./workflowParser/constants";

const { generateUUID } = bridge;

export class UiModelCreator {
    public static getUiModel(workflow: Workflow, selectedSetId?: string): { components: IComponent[]; links: ILink[] } {
        const components: IComponent[] = [];
        const links: ILink[] = [];

        const set = workflow.sets.find(s => s.id === selectedSetId) ?? workflow.sets[0];
        const setsActivities = workflow.activities.filter(activity => activity.positions.some(position => position.set.id === set.id));
        const uiActivitiesIds: number[] = [];

        for (let i = 1; i <= setsActivities.length; i++) {
            uiActivitiesIds.push(i);
        }

        for (let i = 0; i < setsActivities.length; i++) {
            const activitiesPosition = setsActivities[i].positions.find(position => position.set.id === set.id);
            const uiActivity = new UiActivity((i + 1), activitiesPosition!.x, activitiesPosition!.y, activitiesPosition!.width, activitiesPosition!.height, set.id, CONST_ROOT_COMPONENT_INDEX, setsActivities[i]);
            components[i + 1] = uiActivity;
        }

        let linkIndex = 0;
        const linksIndexes = [];

        for (let i = 1; i < components.length; i++) {
            const startComponent = components[i] as UiActivity;

            for (let j = 0; j < startComponent.activity.transitions.length; j++) {
                const transition = startComponent.activity.transitions[j];

                if (transition.setId !== set.id)
                    continue;

                const endComponent = components.find(comp => {
                    if (comp instanceof UiActivity)
                        return comp.activity.id === transition.to;
                    return false;
                });

                if (endComponent == null)
                    throw new Error(`Component of id "${endComponent}" could not be found!`);

                linksIndexes.push(linkIndex);
                links.push(new UiTransition(linkIndex, transition.points, startComponent.index, endComponent.index, transition));
                linkIndex++;
            }
        }

        components[CONST_ROOT_COMPONENT_INDEX] = new UiSet(CONST_ROOT_COMPONENT_INDEX, uiActivitiesIds, linksIndexes, set, workflow);

        return { components, links };
    }

    public static getWorkflowEntities(uiWorkflow: UiWorkflow): { sets: ISet[]; activities: IActivity[]; transitions: ITransition[]; definitionParticipants: IParticipant[] } {
        const activities: IActivity[] = [];
        const transitions: ITransition[] = [];
        const sets: ISet[] = [];
        const definitionParticipants: IParticipant[] = [];

        uiWorkflow.picassoModel.components.forEach(element => {
            if (element.index === CONST_ROOT_COMPONENT_INDEX) {
                const uiSet = element as UiSet;

                uiSet.workflow.sets.forEach(({ id, name, description }) => {
                    sets.push({ id, name, description });
                });

                uiSet.workflow.definitionParticipants.forEach(({ identifier, name, condition, roleName }) => {
                    definitionParticipants.push({
                        locKey: LOC_DEFINITION_PARTICIPANT,
                        identifier,
                        participantType: ParticipantType.DefinitionParticipant,
                        name,
                        properties: { identifier, name, condition, roleName },
                    });
                });

                return;
            }

            const uiActivity = element as UiActivity;
            const actions: IAction[] = [];
            const participants: IParticipant[] = [];
            const activeParticipants: IParticipant[] = [];
            const triggers: ITrigger[] = [];
            const positions: IPosition[] = [];

            uiActivity.activity.actions.forEach((elmnt) => {
                this.getActionType(elmnt, actions);
            });

            uiActivity.activity.participants.forEach((participant) => {
                if (participant instanceof DefinitionParticipant)
                    participants.push(UiModelCreator.createDefinitionParticipant(participant));
                else if (participant instanceof Owner)
                    participants.push(UiModelCreator.createOwnerParticipant());
                else if (participant instanceof System)
                    participants.push(UiModelCreator.createSystemParticipant());
                else if (participant instanceof Role)
                    participants.push(UiModelCreator.createRoleParticipant(participant.name, participant.dbID.toString()));
                else if (participant instanceof User)
                    participants.push(UiModelCreator.createUserParticipant(participant.name, participant.dbID.toString()));
                else
                    throw new Error("Participant type not implemented!");
            });

            uiActivity.activity.activeParticipants.forEach((activeParticipant) => {
                if (activeParticipant.type === ParticipantType.DefinitionParticipant)
                    activeParticipants.push({ identifier: ParticipantType.DefinitionParticipant, participantType: ParticipantType.DefinitionParticipant, locKey: LOC_DEFINITION_PARTICIPANT, name: activeParticipant.name, properties: {} });
                else if (activeParticipant.type === ParticipantType.Owner)
                    activeParticipants.push(UiModelCreator.createOwnerParticipant());
                else if (activeParticipant.type === ParticipantType.System)
                    activeParticipants.push(UiModelCreator.createSystemParticipant());
                else if (activeParticipant.type === ParticipantType.Role)
                    activeParticipants.push(UiModelCreator.createRoleParticipant(activeParticipant.name, activeParticipant.id));
                else if (activeParticipant.type === ParticipantType.User)
                    activeParticipants.push(UiModelCreator.createUserParticipant(activeParticipant.name, activeParticipant.id));
                else
                    throw new Error(`Active participant type '${activeParticipant.type}' not implemented!`);
            });

            uiActivity.activity.triggers.forEach((trigger) => {
                if (trigger instanceof AttributeDataChange)
                    triggers.push(UiModelCreator.createAttributeDataChange(trigger));
                else if (trigger instanceof Menu)
                    triggers.push(UiModelCreator.createMenu(trigger));
                else if (trigger instanceof NewObject)
                    triggers.push(UiModelCreator.createNewObject());
                else if (trigger instanceof WfMessage)
                    triggers.push(UiModelCreator.createWfMessage(trigger));
                else
                    throw new Error("Trigger type not implemented!");
            });

            uiActivity.activity.positions.forEach((position) => {
                positions.push({
                    setId: position.set.id,
                    setName: position.set.name,
                });
            });

            activities.push({
                picassoIndex: element.index,
                id: uiActivity.activity.id,
                condition: uiActivity.activity.condition,
                runCondition: uiActivity.activity.runCondition,
                type: uiActivity.activity.type,
                description: uiActivity.activity.description,
                inheritParticipants: uiActivity.activity.inheritParticipants,
                killChildren: uiActivity.activity.killChildren,
                name: uiActivity.activity.name,
                order: uiActivity.activity.order,
                inputTransType: uiActivity.activity.inputTransType,
                outputTransType: uiActivity.activity.outputTransType,
                showInWfHistory: uiActivity.activity.showInWfHistory,
                isActive: uiActivity.activity.isActive,
                actions: actions,
                participants: participants,
                activeParticipants: activeParticipants,
                triggers: triggers,
                positions: positions,
            });
        });

        uiWorkflow.picassoModel.links.forEach(element => {
            const { transition, index, startComponentIndex, endComponentIndex } = element as UiTransition;
            const actions: IAction[] = [];

            transition.actions.forEach((act) => {
                this.getActionType(act, actions);
            });

            transitions.push({
                id: transition.id,
                picassoIndex: index,
                condition: transition.condition,
                name: transition.name,
                setId: transition.setId,
                toIndex: endComponentIndex,
                fromIndex: startComponentIndex,
                isActive: transition.isActive,
                actions,
            });
        });

        return { sets, activities, transitions, definitionParticipants };
    }

    private static getActionType(action: Action, actions: IAction[]): void {
        if (action instanceof CallMethod)
            actions.push(UiModelCreator.createCallMethodAction(action));
        else if (action instanceof CallScript)
            actions.push(UiModelCreator.createCallScriptAction(action));
        else if (action instanceof IncrementDateTime)
            actions.push(UiModelCreator.createIncrementDateTimeAction(action));
        else if (action instanceof IncrementNumber)
            actions.push(UiModelCreator.createIncrementNumberAction(action));
        else if (action instanceof SendMail)
            actions.push(UiModelCreator.createSendMailAction(action));
        else if (action instanceof SendSMS)
            actions.push(UiModelCreator.createSendSmsAction(action));
        else if (action instanceof SendWFMessage)
            actions.push(UiModelCreator.createSendWfMessageAction(action));
        else if (action instanceof SetAttribute)
            actions.push(UiModelCreator.createSetAttributeAction(action));
        else if (action instanceof SetObjectAttribute)
            actions.push(UiModelCreator.createSetObjectAttributeAction(action));
        else if (action instanceof SetValue)
            actions.push(UiModelCreator.createSetValueAction(action));
        else if (action instanceof OpenForm)
            actions.push(UiModelCreator.createOpenFormAction(action));
        else
            throw new Error("Action type not implemented!");
    }

    public static createSystemParticipant(): IParticipant {
        return {
            identifier: ParticipantType.System,
            locKey: LOC_SYSTEM,
            participantType: ParticipantType.System,
            name: "",
            properties: EMPTY_OBJECT,
        };
    }

    public static createOwnerParticipant(): IParticipant {
        return {
            identifier: ParticipantType.Owner,
            locKey: LOC_OWNER,
            participantType: ParticipantType.Owner,
            name: "",
            properties: EMPTY_OBJECT,
        };
    }

    private static createDefinitionParticipant(participant: DefinitionParticipant): IParticipant {
        return {
            participantType: ParticipantType.DefinitionParticipant,
            locKey: LOC_DEFINITION_PARTICIPANT,
            identifier: participant.identifier,
            name: participant.name,
            properties: {
                [CONST_DEFINITION_PARTICIPANT_NAME_AT]: participant.name,
                [CONST_DEFINITION_PARTICIPANT_ROLE_NAME_AT]: participant.roleName,
                [CONST_DEFINITION_PARTICIPANT_CONDITION_AT]: participant.condition,
            },
        };
    }

    public static createRoleParticipant(name: string, dbID: string): IParticipant {
        return {
            participantType: ParticipantType.Role,
            locKey: LOC_ROLE,
            identifier: ParticipantType.Role + dbID,
            name,
            properties: {
                name,
                dbID,
            },
        };
    }

    public static createUserParticipant(name: string, dbID: string): IParticipant {
        return {
            participantType: ParticipantType.User,
            locKey: LOC_USER,
            identifier: ParticipantType.User + dbID,
            name,
            properties: {
                name,
                dbID,
            },
        };
    }

    public static createCallMethodAction(action?: CallMethod): IAction {
        return {
            actionType: ActionType.CallMethod,
            locKey: LOC_EXECUTING_METHOD,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_CALL_METHOD_METHOD_NAME_AT]: action?.methodName ?? "",
                [CONST_CALL_METHOD_STATIC_AT]: action?.static.toString() ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT]: action?.defaultParamValue ?? "",
                [CONST_CALL_METHOD_INSERT_ACTUAL_OBJECT_AT]: action?.insertActualObject.toString() ?? "",
                [CONST_CALL_METHOD_INSERT_PARAM_FORM_OBJECT_AT]: action?.insertParamFormObject.toString() ?? "",
            },
        };
    }

    public static createCallScriptAction(action?: CallScript): IAction {
        return {
            actionType: ActionType.CallScript,
            locKey: LOC_START_OF_SCRIPT,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_CALL_SCRIPT_SCRIPT_AT]: action?.script ?? "",
            },
        };
    }

    public static createIncrementDateTimeAction(action?: IncrementDateTime): IAction {
        return {
            actionType: ActionType.IncrementValue,
            locKey: LOC_INCREMENTING_VALUE,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_INCREMENT_DATE_TIME_PRECISION_AT]: action?.precision ?? DateTimePrecision.day,
                [CONST_INCREMENT_DATE_TIME_ATTR_NAME_AT]: action?.attrName ?? "",
                [CONST_INCREMENT_DATE_TIME_VALUE_AT]: action?.value.toString() ?? "",
            },
        };
    }

    public static createIncrementNumberAction(action?: IncrementNumber): IAction {
        return {
            actionType: ActionType.IncrementValue,
            locKey: LOC_INCREMENTING_VALUE,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_INCREMENT_DATE_TIME_PRECISION_AT]: action?.precision ?? DateTimePrecision.number, // todo, je to potřeba? nšlo by jinak?
                [CONST_INCREMENT_NUMBER_ATTR_NAME_AT]: action?.attrName ?? "",
                [CONST_INCREMENT_NUMBER_VALUE_AT]: action?.value.toString() ?? "",
            },
        };
    }

    public static createSendMailAction(action?: SendMail): IAction {
        return {
            actionType: ActionType.SendMail,
            locKey: LOC_EMAIL,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_SEND_MAIL_RECIPIENT_TYPE_AT]: action?.recipientType ?? SendMessageRecipientType.value,
                [CONST_SEND_MAIL_RECIPIENT_AT]: action?.recipient ?? "",
                [CONST_SEND_MAIL_CLASS_NAME_AT]: action?.className ?? "",
                [CONST_SEND_MAIL_ATTR_NAME_AT]: action?.attrName ?? "",
                [CONST_SEND_MAIL_DATA_CONDITION_AT]: action?.dataCondition ?? "",
                [CONST_SEND_MAIL_SUBJECT_AT]: action?.subject ?? "",
                [CONST_SEND_MAIL_TEXT_AT]: action?.text ?? "",
                [CONST_SEND_MAIL_REPORT_TEMPLATE_AT]: action?.reportTemplate ?? "",
                [CONST_SEND_MAIL_SUMMARY_MAIL_AT]: action?.summaryMail.toString() ?? "",
                [CONST_SEND_MAIL_FORMAT_AT]: action?.format ?? MessageFormat.text,
                [CONST_SEND_MAIL_DOCUMENT_CLASS_NAME_AT]: action?.documentClassName ?? "",
                [CONST_SEND_MAIL_DOCUMENT_ATTRIBUTE_AT]: action?.documentAttribute ?? "",
                [CONST_SEND_MAIL_DOCUMENT_CONDITION_AT]: action?.documentCondition ?? "",
                [CONST_SEND_MAIL_DOCUMENT_CONDITION_PARAMS_AT]: action?.documentConditionParams ?? "",
                [CONST_SEND_MAIL_SEND_POSTPONE_AT]: action?.sendPostpone.toString() ?? "",
            },
        };
    }

    public static createSendSmsAction(action?: SendSMS): IAction {
        return {
            actionType: ActionType.SendSMS,
            locKey: LOC_SMS,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_SEND_SMS_CLASS_NAME_AT]: action?.className ?? "",
                [CONST_SEND_SMS_ATTR_NAME_AT]: action?.attrName ?? "",
                [CONST_SEND_SMS_DATA_CONDITION_AT]: action?.dataCondition ?? "",
                [CONST_SEND_SMS_RECIPIENT_TYPE_AT]: action?.recipientType ?? SendSmsRecipientType.value,
                [CONST_SEND_SMS_TEXT_AT]: action?.text ?? "",
                [CONST_SEND_SMS_RECIPIENT_AT]: action?.recipient ?? "",
            },
        };
    }

    public static createSendWfMessageAction(action?: SendWFMessage): IAction {
        return {
            actionType: ActionType.SendWFMessage,
            locKey: LOC_SENDING_OF_WF_MESSAGE,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_SEND_WF_MESSAGE_AFTER_SAVE_AT]: action?.afterSave.toString() ?? "",
                [CONST_SEND_WF_MESSAGE_MESSAGE_NAME_AT]: action?.messageName ?? "",
                [CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_AT]: action?.objectsCondition ?? "",
                [CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_PARAMS_AT]: action?.objectsConditionParams ?? "",
                [CONST_SEND_WF_MESSAGE_TARGET_OBJECT_NAME_AT]: action?.targetObjectName ?? "",
            },
        };
    }

    public static createSetAttributeAction(action?: SetAttribute): IAction {
        return {
            actionType: ActionType.SetAttribute,
            locKey: LOC_ATTRIBUTE_CHANGE,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_SET_ATTRIBUTE_VALUE_TYPE]: action?.valueType ?? "",
                [CONST_SET_ATTRIBUTE_VALUE_EL]: action?.value ?? "",
                [CONST_SET_ATTRIBUTE_ATTR_NAME_AT]: action?.attrName ?? "",
            },
        };
    }

    public static createSetObjectAttributeAction(action?: SetObjectAttribute): IAction {
        return {
            actionType: ActionType.SetObjectAttribute,
            locKey: LOC_CHANGE_OF_BINDING_ATTRIBUTE,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_SET_OBJECT_ATTRIBUTE_VALUE_EL]: action?.value ?? "",
                [CONST_SET_OBJECT_ATTRIBUTE_ATTR_NAME_AT]: action?.attrName ?? "",
                [CONST_SET_OBJECT_ATTRIBUTE_MASTER_CLASS_NAME_AT]: action?.masterClassName ?? "",
                [CONST_SET_OBJECT_ATTRIBUTE_MASTER_ATTR_NAME_AT]: action?.masterAttrName ?? "",
            },
        };
    }

    public static createSetValueAction(action?: SetValue): IAction {
        return {
            actionType: ActionType.SetValue,
            locKey: LOC_SETTING_VALUE,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_SET_VALUE_VALUE_EL]: action?.value ?? "",
                [CONST_SET_VALUE_NAME_AT]: action?.name ?? "",
            },
        };
    }

    public static createOpenFormAction(action?: OpenForm): IAction {
        return {
            actionType: ActionType.OpenForm,
            locKey: LOC_FORM_OPENING,
            name: "",
            properties: {
                [CONST_ACTION_COMMENT_AT]: action?.comment ?? "",
                [CONST_ACTION_CONDITION_AT]: action?.condition ?? "",
                [CONST_OPEN_FORM_FORM_CLASS_AT]: action?.formClass ?? "",
                [CONST_OPEN_FORM_FORM_CONDITION_AT]: action?.formCondition ?? "",
                [CONST_OPEN_FORM_FORM_TYPE_AT]: action?.formType ?? FormType.form,
                [CONST_OPEN_FORM_MASTER_LINK_TO_ATTR_AT]: action?.masterLinkToAttr ?? "",
                [CONST_OPEN_FORM_REPORT_NAME_AT]: action?.reportName ?? "",
            },
        };
    }

    public static createAttributeDataChange(trigger?: AttributeDataChange): ITrigger {
        return {
            triggerType: TriggerType.AttributeDataChange,
            locKey: LOC_ATTRIBUTE_VALUE_CHANGE,
            name: "",
            properties: {
                [CONST_ATTRIBUTE_DATA_CHANGE_ATTR_NAME_AT]: trigger?.attrName ?? "",
                [CONST_ATTRIBUTE_DATA_CHANGE_CONDITION_AT]: trigger?.condition ?? "",
            },
        };
    }

    public static createMenu(trigger?: Menu): ITrigger {
        return {
            triggerType: TriggerType.Menu,
            locKey: LOC_MENU,
            name: "",
            properties: {
                [CONST_MENU_IDENTIFIER_AT]: trigger?.identifier ?? generateUUID(),
                [CONST_MENU_CODE_NAME]: trigger?.codename ?? "",
                [CONST_MENU_ICON_NAME]: trigger?.iconName ?? "",
                [CONST_MENU_MASTER_NODE_AT]: trigger?.masterNode ?? "",
                [CONST_MENU_PARAM_FORM_NAME_AT]: trigger?.paramFormName ?? "",
                [CONST_MENU_LINK_ATTR_FOR_SEL_OBJ_AT]: trigger?.linkAttrForSelObj ?? "",
                [CONST_MENU_SET_VALUES_AT]: trigger?.setValues ?? "",
                [CONST_MENU_MULTI_ACTION_ENABLE_AT]: trigger?.multiActionEnable.toString() ?? "",
                [CONST_MENU_CHOOSE_FROM_USERS_DEFAULT]: trigger?.chooseFromUsersDefault.toString() ?? "",
                [CONST_MENU_PARTICIPANTS_SELECTING_AT]: trigger?.participantsSelecting.toString() ?? "",
                [CONST_MENU_VALIDATE_POST_REQ_AT]: trigger?.validatePostReq.toString() ?? "",
                [CONST_MENU_VALIDATE_RECOMMENDED_AT]: trigger?.validateRecommended.toString() ?? "",
                [CONST_MENU_CAPTIONS_EL]: JSON.stringify(trigger?.captions ?? []),
                [CONST_MENU_CONFIRM_MESSAGES_EL]: JSON.stringify(trigger?.confirmMessages ?? []),
            },
        };
    }

    public static createNewObject(): ITrigger {
        return {
            triggerType: TriggerType.NewObject,
            locKey: LOC_CREATION_OF_DOCUMENT,
            name: "",
            properties: EMPTY_OBJECT,
        };
    }

    public static createWfMessage(trigger?: WfMessage): ITrigger {
        return {
            triggerType: TriggerType.WfMessage,
            locKey: LOC_WORKFLOW_MESSAGE,
            name: "",
            properties: {
                [CONST_WFMESSAGE_MESSAGE_NAME_AT]: trigger?.messageName ?? "",
                [CONST_WFMESSAGE_DEACTIVATING_AT]: trigger?.deactivating.toString() ?? "",
            },
        };
    }
}
